import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface ValidatedSnItemResponse {
  success: boolean;
  data?: Array<{ inventoryItemId: number; segment1: string }>;
  error?: string;
}

interface ValidatedSnItemParam {
  serial_number: string;
}

export const useValidatedSnItem = (
  serialNumber?: string,
  executeApiOnload = false
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [data, setData] = useState<string>("");

  const fetchData = async (
    params: ValidatedSnItemParam,
    callBackFn?: (args?: string) => void
  ) => {
    setIsLoading(true);
    setData("");

    try {
      if (!params.serial_number?.trim()) {
        callBackFn && callBackFn("");
        return;
      }

      const response = await apiClient.get<ValidatedSnItemResponse>(
        API_CONFIG.ENDPOINTS.GET_VALIDATED_SN_ITEM,
        {
          params: {
            serial_number: String(params.serial_number || '').trim(),
          },
        }
      );

      let parsedData = "";
      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        parsedData = response.data.data[0].segment1 || "";
      }

      setData(parsedData);
      callBackFn && callBackFn(parsedData);
    } catch (error: any) {
      console.error(error);
      setError(error);
      callBackFn && callBackFn("");
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = (serialNum: string, callBackFn?: (args?: string) => void) => {
    fetchData({ serial_number: serialNum }, callBackFn);
  };

  const fetchValidatedSnItem = useCallback(async (serialNum: string): Promise<string> => {
    try {
      if (!serialNum?.trim()) {
        return "";
      }

      const response = await apiClient.get<ValidatedSnItemResponse>(
        API_CONFIG.ENDPOINTS.GET_VALIDATED_SN_ITEM,
        {
          params: {
            serial_number: String(serialNum || '').trim(),
          },
        }
      );

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        return response.data.data[0].segment1 || "";
      }
      return "";
    } catch (error: any) {
      console.error('Error fetching validated serial item:', error);
      return "";
    }
  }, []);

  useEffect(() => {
    if (executeApiOnload && serialNumber) {
      fetchData({ serial_number: serialNumber });
    }
  }, [executeApiOnload, serialNumber]);

  return { data, isLoading, error, refetch, fetchValidatedSnItem };
};
